/**
 * Blog Listing Page
 * Clean. Scannable. Content-first.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Body, Label } from '../components/primitives/Typography';
import { PageLoader } from '../components/common/LoadingSpinner';
import type { BlogPost } from '../types';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const currentCategory = searchParams.get('category') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    loadPosts();
    loadCategories();
  }, [currentCategory, currentPage]);

  async function loadPosts() {
    setLoading(true);
    try {
      const res = await api.getBlogPosts({
        page: currentPage,
        limit: 10,
        category: currentCategory || undefined,
      });
      setPosts(res.posts);
      setPagination(res.pagination);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadCategories() {
    try {
      const res = await api.getBlogCategories();
      setCategories(res.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCategoryClick = (category: string) => {
    if (category === currentCategory) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const handlePageChange = (page: number) => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main>
      {/* Hero */}
      <Section size="lg" className="pt-20 md:pt-32 pb-16 md:pb-24">
        <Container>
          <div className="max-w-3xl">
            <Label>Blog</Label>
            <Headline className="mt-4">
              Insights for your return journey
            </Headline>
            <Body className="mt-6 text-lg text-text-secondary">
              Practical guides on tax planning, real estate, and navigating the transition back to India.
            </Body>
          </div>
        </Container>
      </Section>

      {/* Categories */}
      {categories.length > 0 && (
        <Section size="sm" background="subtle" className="py-6">
          <Container>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleCategoryClick('')}
                className={`px-4 py-2 text-small font-medium transition-colors ${
                  !currentCategory
                    ? 'bg-text-primary text-white'
                    : 'bg-white text-text-secondary hover:text-text-primary border border-border-subtle'
                }`}
              >
                All posts
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => handleCategoryClick(cat.category)}
                  className={`px-4 py-2 text-small font-medium transition-colors ${
                    currentCategory === cat.category
                      ? 'bg-text-primary text-white'
                      : 'bg-white text-text-secondary hover:text-text-primary border border-border-subtle'
                  }`}
                >
                  {cat.category} ({cat.count})
                </button>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Posts */}
      <Section>
        <Container>
          {loading ? (
            <PageLoader />
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-text-secondary">No posts found.</p>
            </div>
          ) : (
            <>
              <div className="space-y-12">
                {posts.map((post) => (
                  <article key={post.id} className="group">
                    <Link to={`/blog/${post.slug}`}>
                      <div className="grid md:grid-cols-12 gap-6 md:gap-8">
                        {post.featured_image_url && (
                          <div className="md:col-span-4">
                            <div className="aspect-[16/10] bg-surface-subtle overflow-hidden">
                              <img
                                src={post.featured_image_url}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          </div>
                        )}
                        <div className={post.featured_image_url ? 'md:col-span-8' : 'md:col-span-12'}>
                          <div className="flex items-center gap-3 text-small text-text-tertiary mb-3">
                            <time>{formatDate(post.published_at || post.created_at)}</time>
                            {post.category && (
                              <>
                                <span>&middot;</span>
                                <span>{post.category}</span>
                              </>
                            )}
                          </div>
                          <h2 className="text-h3 font-medium text-text-primary group-hover:text-accent transition-colors">
                            {post.title}
                          </h2>
                          {post.excerpt && (
                            <p className="mt-3 text-base text-text-secondary line-clamp-2">
                              {post.excerpt}
                            </p>
                          )}
                          <span className="inline-block mt-4 text-small font-medium text-accent group-hover:underline">
                            Read more
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-16 flex items-center justify-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 text-small font-medium text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 text-small font-medium transition-colors ${
                          page === pagination.page
                            ? 'bg-text-primary text-white'
                            : 'text-text-secondary hover:text-text-primary'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-4 py-2 text-small font-medium text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      <Spacer size="lg" />
    </main>
  );
}
